import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CalendarClock, Gift, Plus, Users } from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorNote,
  Field,
  Input,
  Pagination,
  Select,
  Skeleton,
} from '../../components/ui';
import { api, compact } from '../../lib/api';
import { formatDateTime, formatNumber } from '../../lib/format';
import { useT } from '../../lib/i18n';
import type { Paginated, ReferralCampaign } from '../../lib/types';
import { CAMPAIGN_TONES, describeOffer } from './shared';

/** The campaigns, newest first, each as the sentence it promises. */
export function CampaignsTab({
  onOpen,
  onCreate,
}: {
  onOpen: (campaignId: string) => void;
  onCreate: () => void;
}) {
  const t = useT();
  const [status, setStatus] = useState('');
  const [audience, setAudience] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const query = useMemo(
    () => compact({ status, audience, search: search.trim(), page, limit: 20 }),
    [status, audience, search, page],
  );

  const campaigns = useQuery({
    queryKey: ['referrals', 'campaigns', query],
    queryFn: () => api.get<Paginated<ReferralCampaign>>('/api/referrals/campaigns', query),
  });

  const filter = (apply: () => void) => {
    apply();
    setPage(1);
  };

  return (
    <div className="space-y-4">
      <div className="grid gap-3 rounded-xl border border-line bg-surface p-3.5 sm:grid-cols-3 sm:p-4">
        <Field label={t('referrals.filter.status')}>
          <Select value={status} onChange={(event) => filter(() => setStatus(event.target.value))}>
            <option value="">{t('referrals.filter.anyStatus')}</option>
            {(['live', 'scheduled', 'ended'] as const).map((value) => (
              <option key={value} value={value}>
                {t(`referrals.status.${value}`)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('referrals.filter.audience')}>
          <Select
            value={audience}
            onChange={(event) => filter(() => setAudience(event.target.value))}
          >
            <option value="">{t('referrals.filter.anyAudience')}</option>
            <option value="all">{t('referrals.audience.all')}</option>
            <option value="users">{t('referrals.audience.users')}</option>
          </Select>
        </Field>
        <Field label={t('referrals.filter.search')}>
          <Input
            value={search}
            onChange={(event) => filter(() => setSearch(event.target.value))}
            placeholder={t('common.searchPlaceholder')}
          />
        </Field>
      </div>

      {campaigns.isError && <ErrorNote error={campaigns.error} />}

      {campaigns.isPending ? (
        <Skeleton className="h-64" />
      ) : (campaigns.data?.items.length ?? 0) === 0 ? (
        <Card>
          <EmptyState
            icon={<Gift size={28} />}
            title={t('referrals.campaigns.emptyTitle')}
            description={t('referrals.campaigns.emptyDescription')}
            action={
              <Button variant="primary" icon={<Plus size={15} />} onClick={onCreate}>
                {t('referrals.newCampaign')}
              </Button>
            }
          />
        </Card>
      ) : (
        <Card>
          <ul className="divide-y divide-line">
            {campaigns.data?.items.map((campaign) => (
              <li key={campaign.id}>
                <CampaignCard campaign={campaign} onOpen={() => onOpen(campaign.id)} />
              </li>
            ))}
          </ul>
          <Pagination
            page={campaigns.data?.page ?? 1}
            totalPages={campaigns.data?.totalPages ?? 1}
            total={campaigns.data?.total ?? 0}
            onChange={setPage}
          />
        </Card>
      )}
    </div>
  );
}

function CampaignCard({
  campaign,
  onOpen,
}: {
  campaign: ReferralCampaign;
  onOpen: () => void;
}) {
  const t = useT();
  const { stats } = campaign;

  return (
    <button
      type="button"
      onClick={onOpen}
      className="w-full px-4 py-3.5 text-left transition-colors hover:bg-raised sm:px-5"
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="min-w-0 truncate text-sm font-semibold text-ink">
          {campaign.name}
        </span>
        <Badge tone={CAMPAIGN_TONES[campaign.status]}>
          {t(`referrals.status.${campaign.status}`)}
        </Badge>
        <Badge tone={campaign.audience === 'users' ? 'brand' : 'neutral'}>
          {t(`referrals.audience.${campaign.audience}`)}
        </Badge>
        {stats.rewardsFailed > 0 && (
          <Badge tone="danger">
            {t('referrals.campaigns.failedBadge', { count: stats.rewardsFailed })}
          </Badge>
        )}
      </div>

      <p className="mt-1 text-xs text-ink-2">{describeOffer(campaign)}</p>

      <div className="mt-2.5 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-ink-2">
        <span className="inline-flex items-center gap-1">
          <CalendarClock size={13} className="text-ink-3" />
          {campaign.endsAt
            ? t(
                campaign.status === 'ended'
                  ? 'referrals.campaigns.endedOn'
                  : 'referrals.campaigns.endsOn',
                { date: formatDateTime(campaign.endsAt) },
              )
            : t('referrals.neverExpires')}
        </span>
        <span className="tnum inline-flex items-center gap-1">
          <Users size={13} className="text-ink-3" />
          {t('referrals.campaigns.joinedLine', {
            joined: formatNumber(stats.joined),
            qualified: formatNumber(stats.qualified),
          })}
        </span>
        <span className="tnum inline-flex items-center gap-1">
          <Gift size={13} className="text-ink-3" />
          {t('referrals.campaigns.rewardsLine', {
            count: formatNumber(stats.rewardsGranted),
          })}
        </span>
      </div>
    </button>
  );
}
